/**
 * Utility functions for comparing trade data with confirmation data.
 *
 * Phase 3: Comparison results now come from the backend (MatchingService._compare_fields()).
 * This module maps backend results to display rows — it no longer re-computes comparisons.
 */

import {
  ConfirmationData,
  ComparisonField,
  ComparisonResult,
  ExtractedTradeFields,
} from '../types/confirmation';
import { formatAmountDisplay, formatPriceDisplay } from './amountFormatter';

/**
 * Field definition for comparison logic
 */
interface FieldDefinition {
  name: string;
  display: string;
  comparable: boolean;
  condition?: 'Compensación' | 'Entrega Física';
  forwardOnly?: boolean;
  forwardStartingOnly?: boolean;
}

/**
 * Base fields applicable to Spot trades
 * Note: TradeNumber is never compared (client vs bank numbers differ)
 * Note: Portfolio and HedgeObject are client-only, never compared
 * Display values are translation keys under grid.columns.*
 */
const SPOT_FIELDS: FieldDefinition[] = [
  { name: 'TradeNumber', display: 'tradeNumber', comparable: false },
  { name: 'OurParty', display: 'entity', comparable: true },
  { name: 'CounterpartyName', display: 'counterparty', comparable: true },
  { name: 'ProductType', display: 'productType', comparable: true },
  { name: 'TradeDate', display: 'tradeDate', comparable: true },
  { name: 'ValueDate', display: 'valueDate', comparable: true },
  { name: 'Direction', display: 'direction', comparable: true },
  { name: 'Currency1', display: 'currency1', comparable: true },
  { name: 'QuantityCurrency1', display: 'amount', comparable: true },
  { name: 'Price', display: 'price', comparable: true },
  { name: 'Currency2', display: 'currency2', comparable: true },
  { name: 'QuantityCurrency2', display: 'contraAmount', comparable: true },
  { name: 'Currency1PaymentDate', display: 'currency1PaymentDate', comparable: true },
  { name: 'Currency2PaymentDate', display: 'currency2PaymentDate', comparable: true },
];

/**
 * Additional fields for Forward trades
 * Display values are translation keys under grid.columns.*
 */
const FORWARD_ADDITIONAL_FIELDS: FieldDefinition[] = [
  { name: 'MaturityDate', display: 'maturityDate', comparable: true },
  { name: 'FixingReference', display: 'fixingReference', comparable: true },
  { name: 'FixingOffset', display: 'fixingOffset', comparable: true },
  { name: 'AverageRateFixingDates', display: 'fixingDates', comparable: true },
  { name: 'ForwardPoints', display: 'forwardPoints', comparable: true, forwardStartingOnly: true },
  { name: 'SpotFixingReference', display: 'spotFixingReference', comparable: true, forwardStartingOnly: true },
  { name: 'ForwardStartingFixingDates', display: 'forwardStartingFixingDates', comparable: true, forwardStartingOnly: true },
  { name: 'SettlementType', display: 'settlementType', comparable: true },
  { name: 'SettlementCurrency', display: 'settlementCurrency', comparable: true, condition: 'Compensación' },
  { name: 'PaymentDate', display: 'paymentDate', comparable: true, condition: 'Compensación' },
];

/**
 * Convert a single date string to DD/MM/YYYY display format
 */
function toDisplayDate(d: unknown): string {
  const s = String(d);
  // YYYY-MM-DD → DD/MM/YYYY
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) {
    const [y, m, day] = s.split('-');
    return `${day}/${m}/${y}`;
  }
  // dd-mm-yyyy → DD/MM/YYYY
  if (/^\d{2}-\d{2}-\d{4}$/.test(s)) {
    const [day, m, y] = s.split('-');
    return `${day}/${m}/${y}`;
  }
  return s;
}

/**
 * Convert DD/MM/YYYY to YYYYMMDD for chronological sorting
 */
function toSortKey(d: string): string {
  const parts = d.split('/');
  return parts.length === 3 ? `${parts[2]}${parts[1]}${parts[0]}` : d;
}

/**
 * Summarize a list of dates as "N fechas (first – last)" when >2 dates
 */
function summarizeDateList(dates: unknown): string {
  if (!Array.isArray(dates) || dates.length === 0) return '';
  const formatted = dates.map(toDisplayDate);
  formatted.sort((a, b) => toSortKey(a).localeCompare(toSortKey(b)));
  if (formatted.length <= 2) return formatted.join(', ');
  return `${formatted.length} fechas (${formatted[0]} – ${formatted[formatted.length - 1]})`;
}

/**
 * Get the trade's fixing dates from the AverageRateFixingSchedule array of objects
 */
export function getTradeFixingDates(trade: Record<string, unknown>): string[] {
  const schedule = trade.AverageRateFixingSchedule;
  if (!Array.isArray(schedule)) return [];
  return schedule
    .map((entry: unknown) => {
      if (typeof entry === 'object' && entry !== null && 'Date' in entry) {
        return String((entry as Record<string, unknown>).Date);
      }
      return String(entry);
    })
    .filter(Boolean);
}

/**
 * Get the trade's Forward Starting fixing dates from the ForwardStartingFixingSchedule array
 */
function getTradeForwardStartingFixingDates(trade: Record<string, unknown>): string[] {
  const schedule = trade.ForwardStartingFixingSchedule;
  if (!Array.isArray(schedule)) return [];
  return schedule
    .map((entry: unknown) => {
      if (typeof entry === 'object' && entry !== null && 'Date' in entry) {
        return String((entry as Record<string, unknown>).Date);
      }
      if (typeof entry === 'object' && entry !== null && 'date' in entry) {
        return String((entry as Record<string, unknown>).date);
      }
      return String(entry);
    })
    .filter(Boolean);
}

/**
 * Fields that should be displayed as integers (no decimal places)
 */
const INTEGER_FIELDS = ['FixingOffset'];

/**
 * Fields that are prices/rates — use formatPriceDisplay (smart decimals, up to 4)
 */
const PRICE_FIELDS = ['Price', 'ForwardPoints'];

/**
 * Fields that contain dates and should be formatted as DD/MM/YYYY for display
 */
const DATE_FIELDS = [
  'TradeDate', 'ValueDate', 'MaturityDate', 'PaymentDate',
  'Currency1PaymentDate', 'Currency2PaymentDate',
];

/**
 * Format a value for display in the comparison table
 */
function formatValue(value: unknown, fieldName?: string, locale: string = 'es'): string | number | null {
  if (value === null || value === undefined || value === '') return null;
  if (typeof value === 'number') {
    if (fieldName && INTEGER_FIELDS.includes(fieldName)) {
      return Math.round(value);
    }
    if (fieldName && PRICE_FIELDS.includes(fieldName)) {
      return formatPriceDisplay(value, locale);
    }
    return formatAmountDisplay(value, locale);
  }
  if (fieldName && DATE_FIELDS.includes(fieldName)) {
    return toDisplayDate(value);
  }
  return String(value);
}

/**
 * Build comparison fields for a trade vs confirmation.
 *
 * Uses backend-provided discrepancies as the source of truth for comparison results.
 * Frontend handles display concerns: field visibility, not_applicable, formatting, i18n.
 */
export function buildComparisonFields(
  trade: Record<string, unknown>,
  confirmationData: ConfirmationData,
  locale: string = 'es',
): ComparisonField[] {
  const productType = String(trade.ProductType || '').toLowerCase();
  const isForward = productType === 'forward';
  const settlementType = String(trade.SettlementType || '');
  const tradeIsAsiatico = Boolean(trade.IsAverageRateForward);
  const confirmationIsAsiatico = Boolean(confirmationData.extractedFields.IsAverageRateForward);
  const tradeIsForwardStarting = Boolean(trade.IsForwardStarting);
  const confirmationIsForwardStarting = Boolean(confirmationData.extractedFields.IsForwardStarting);

  // Backend-provided comparison results
  const discrepancies = confirmationData.discrepancies || [];
  const comparisonFieldsUsed = confirmationData.comparisonFieldsUsed || [];

  // Build a lookup map: field name → backend result
  const backendResults = new Map<string, string>();
  for (const d of discrepancies) {
    backendResults.set(d.field, d.result);
  }

  // Determine which fields to show
  let fieldDefs = [...SPOT_FIELDS];
  if (isForward) {
    fieldDefs = [...fieldDefs, ...FORWARD_ADDITIONAL_FIELDS];
  }

  const isForwardStarting = tradeIsForwardStarting || confirmationIsForwardStarting;

  return fieldDefs
    .filter((fieldDef) => {
      // Hide FixingOffset for Forward Asiático (not applicable)
      if (fieldDef.name === 'FixingOffset' && tradeIsAsiatico) return false;
      // Hide AverageRateFixingDates for non-Asiático trades
      if (fieldDef.name === 'AverageRateFixingDates' && !tradeIsAsiatico && !confirmationIsAsiatico) return false;
      // Hide Forward Starting-only fields for non-Forward Starting trades
      if (fieldDef.forwardStartingOnly && !isForwardStarting) return false;
      return true;
    })
    .map((fieldDef) => {
      // Check if field is in the backend's configured comparison set
      const isConfigured = comparisonFieldsUsed.some(
        f => f.toLowerCase() === fieldDef.name.toLowerCase()
      );

      // Check if field is applicable based on condition (frontend display concern)
      let isApplicable = true;
      if (fieldDef.condition) {
        if (fieldDef.condition === 'Compensación') {
          isApplicable = settlementType === 'Compensación' || settlementType === 'COMPENSACION';
        } else if (fieldDef.condition === 'Entrega Física') {
          isApplicable = settlementType === 'Entrega Física' || settlementType === 'ENTREGA_FISICA';
        }
      }
      // Forward-specific Currency1/2PaymentDate conditions
      if (
        isForward &&
        (fieldDef.name === 'Currency1PaymentDate' || fieldDef.name === 'Currency2PaymentDate')
      ) {
        isApplicable = settlementType === 'Entrega Física' || settlementType === 'ENTREGA_FISICA';
      }

      // Determine comparison result — backend is source of truth
      let comparisonResult: ComparisonResult;
      if (!isApplicable) {
        comparisonResult = 'not_applicable';
      } else if (!fieldDef.comparable || !isConfigured) {
        comparisonResult = 'not_compared';
      } else {
        const backendResult = backendResults.get(fieldDef.name);
        if (backendResult === 'match' || backendResult === 'mismatch' || backendResult === 'not_provided') {
          comparisonResult = backendResult;
        } else {
          comparisonResult = 'not_compared';
        }
      }

      // --- Display values (frontend formatting only) ---

      // Special case: ProductType merges with IsAverageRateForward / IsForwardStarting
      if (fieldDef.name === 'ProductType') {
        const tradeDisplay = tradeIsForwardStarting
          ? 'Forward (Starting)'
          : tradeIsAsiatico ? 'Forward (Asiático)' : String(trade.ProductType || '');
        const confirmationDisplay = confirmationIsForwardStarting
          ? 'Forward (Starting)'
          : confirmationIsAsiatico
            ? 'Forward (Asiático)'
            : String(confirmationData.extractedFields.ProductType || '');

        // KAN-205a: If the backend detected an IsAverageRateForward mismatch, propagate it
        // to the ProductType row. ProductType alone cannot distinguish a Normal Forward from a
        // Fwd Asiático (both extract as "Forward"), so the mismatch lives on IsAverageRateForward.
        // Displaying it on the ProductType row makes the discrepancy visible to the user as
        // "Forward" vs "Forward (Asiático)".
        const iarResult = backendResults.get('IsAverageRateForward');
        const productTypeResult: ComparisonResult =
          iarResult === 'mismatch' ? 'mismatch' : comparisonResult;

        return {
          fieldName: fieldDef.name,
          displayName: fieldDef.display,
          tradeValue: tradeDisplay,
          confirmationValue: confirmationDisplay || null,
          isConfiguredForComparison: isConfigured && fieldDef.comparable,
          comparisonResult: productTypeResult,
        };
      }

      // Special case: SpotFixingReference — trade now stores SpotFixingReference directly.
      // Fallback to FixingReference for backward compatibility with trades booked before the fix.
      if (fieldDef.name === 'SpotFixingReference') {
        const tradeValue = trade.SpotFixingReference || trade.FixingReference;
        const confirmationValue = confirmationData.extractedFields.SpotFixingReference;
        return {
          fieldName: fieldDef.name,
          displayName: fieldDef.display,
          tradeValue: formatValue(tradeValue, fieldDef.name, locale),
          confirmationValue: formatValue(confirmationValue, fieldDef.name, locale),
          isConfiguredForComparison: isConfigured && fieldDef.comparable,
          comparisonResult,
        };
      }

      // Special case: ForwardStartingFixingDates — trade has schedule array, confirmation has date range
      if (fieldDef.name === 'ForwardStartingFixingDates') {
        const tradeDates = getTradeForwardStartingFixingDates(trade);
        const confStart = confirmationData.extractedFields.fixingDateRangeStart;
        const confEnd = confirmationData.extractedFields.fixingDateRangeEnd;
        let confirmationDisplay = '';
        if (confStart && confEnd) {
          confirmationDisplay = confStart === confEnd
            ? toDisplayDate(confStart)
            : `${toDisplayDate(confStart)} – ${toDisplayDate(confEnd)}`;
        }
        return {
          fieldName: fieldDef.name,
          displayName: fieldDef.display,
          tradeValue: summarizeDateList(tradeDates) || null,
          confirmationValue: confirmationDisplay || null,
          isConfiguredForComparison: isConfigured && fieldDef.comparable,
          comparisonResult,
        };
      }

      // Special case: AverageRateFixingDates (summarized display)
      if (fieldDef.name === 'AverageRateFixingDates') {
        const tradeDates = getTradeFixingDates(trade);
        const confirmationDates = confirmationData.extractedFields.AverageRateFixingDates || [];

        return {
          fieldName: fieldDef.name,
          displayName: fieldDef.display,
          tradeValue: summarizeDateList(tradeDates) || null,
          confirmationValue: summarizeDateList(confirmationDates) || null,
          isConfiguredForComparison: isConfigured && fieldDef.comparable,
          comparisonResult,
        };
      }

      // Default: read values from trade + extractedFields for display
      const tradeValue = trade[fieldDef.name];
      const confirmationFieldName = fieldDef.name === 'TradeNumber' ? 'BankTradeNumber' : fieldDef.name;
      const confirmationValue =
        confirmationData.extractedFields[confirmationFieldName as keyof ExtractedTradeFields];

      return {
        fieldName: fieldDef.name,
        displayName: fieldDef.display,
        tradeValue: formatValue(tradeValue, fieldDef.name, locale),
        confirmationValue: formatValue(confirmationValue, fieldDef.name, locale),
        isConfiguredForComparison: isConfigured && fieldDef.comparable,
        comparisonResult,
        condition: fieldDef.condition,
      };
    });
}

/**
 * Check if a confirmation has any mismatches
 */
export function hasDiscrepancies(comparisonFields: ComparisonField[]): boolean {
  return comparisonFields.some(
    (field) =>
      field.isConfiguredForComparison &&
      (field.comparisonResult === 'mismatch' || field.comparisonResult === 'not_provided')
  );
}

/**
 * Count the number of mismatches in a comparison
 */
export function countMismatches(comparisonFields: ComparisonField[]): number {
  return comparisonFields.filter(
    (field) => field.isConfiguredForComparison && field.comparisonResult === 'mismatch'
  ).length;
}

/**
 * Count the number of fields not provided by the bank
 */
export function countNotProvided(comparisonFields: ComparisonField[]): number {
  return comparisonFields.filter(
    (field) => field.isConfiguredForComparison && field.comparisonResult === 'not_provided'
  ).length;
}
